import { useState, useRef, useEffect } from "react";
import type { FlagCategory } from "./flagConstants.";
import { getFlagReasons } from "../../api/controllerDashboardApis"
import type { FlagReason } from "../../api/controllerDashboardApis"
import { ChevronDown, Loader2 } from "lucide-react";
import './icons.css'

const CATEGORY_TO_API_NAME: Record<FlagCategory, string> = {
  PII:        "PII Disclosure",
  Conduct:    "Conduct",
  Witness:    "Witness",
  Restricted: "Restricted",
  Technical:  "Technical",
};

interface Props {
  category: FlagCategory | null;
  value: string;
  onChange: (v: string) => void;
}


export function ReasonDropdown({ category, value, onChange }: Props) {
  const [options, setOptions] = useState<FlagReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const selectRef   = useRef<HTMLSelectElement>(null);
  const fetchedFor  = useRef<FlagCategory | null>(null);
  useEffect(() => {
    onChange(""); 
    setError(null);

    if (!category) {
      setOptions([]);
      fetchedFor.current = null;
      return;
    }
    if (fetchedFor.current === category) return;

    const apiName = CATEGORY_TO_API_NAME[category];
    console.log(
      `[ReasonDropdown] category changed → "${category}", API name → "${apiName}"`
    );


    setLoading(true);
    getFlagReasons(apiName)
      .then((fetched) => {
        console.log("[ReasonDropdown] fetched reasons:", fetched);
        setOptions(fetched);
        if (fetched.length === 0) {
          console.warn("[ReasonDropdown] API returned 0 options for category:", category);
        }
        fetchedFor.current = category;
      })
      .catch((err) => {
        console.error("[ReasonDropdown] API error:", err);
        setError("Could not load reasons from server. Please try again.");
        setOptions([]);
        fetchedFor.current = category;
      })
      .finally(() => setLoading(false));
  }, [category]);

  const isDisabled = !category || loading;

  return (
    <div>
      <div
        className="section-label"
        style={{ display: category ? "block" : "none", marginTop: 2 }}
      >
        Specific reason
        {loading && (
          <Loader2
            size={11}
            className="inline ml-1 animate-spin"
            style={{ color: "rgba(255,255,255,0.4)", verticalAlign: "middle" }}
          />
        )}
      </div>

      {error && (
        <div
          style={{
            fontSize: 11,
            color: "#F87171",
            marginBottom: 4,
            paddingLeft: 2,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
  <select
    ref={selectRef}
    value={value}
    onChange={(e) => {
      console.log("[ReasonDropdown] reason selected:", e.target.value);
      onChange(e.target.value);
    }}
    disabled={isDisabled}
    className={`desc-box ${category ? "show" : ""}`}
    style={{
      cursor: isDisabled ? "not-allowed" : "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      paddingRight: 28,
      opacity: loading ? 0.6 : 1,
      transition: "opacity 0.2s",
    }}
  >
    <option value="" disabled>
      {loading ? "Loading reasons…" : "Select specific reason…"}
    </option>

    {options.map((opt) => (
      <option key={opt.id} value={opt.label}>
        {opt.label}
      </option>
    ))}
  </select>


  {!loading && (
    <ChevronDown
      size={16}
      color="rgba(255,255,255,0.4)"
      style={{
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        opacity: isDisabled ? 0.2 : 0.4,
      }}
    />
  )}
</div>
    </div>
  );
}